import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, UntypedFormArray, UntypedFormGroup, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectChange } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ChipsGridRowComponent,
  ConfirmDialogComponent,
  ConfirmDialogService,
  MatSelectSearchComponent,
  TaggingOptions,
  UiKitModule,
} from '@serviceplan/ui-kit';
import {
  ActionsModel,
  AspectRationFormatTree,
  AssetListFileObject,
  AssetTagsService,
  AssetUIModel,
  ClientCoreModule,
  ClientEnvironment,
  CommonUtilityLib,
  CoreAssetsTagsService,
  ENVIRONMENT,
  FileObject,
  FormArrayMapping,
  FormControlModel,
  LufthansaGroupID,
  OptionsLabelPipe,
  PROMPTS,
  StepperGroupModel,
  StepperModel,
  TagsDataConfig,
  addTagToArray,
  anyAsset,
} from '@thehubz/client/core';
import {
  Asset,
  AssetDetails,
  AssetSearchResult,
  AssetsApiService,
  BuyoutRightFields,
  BuyoutRights,
  Collection,
  CollectionResultResponse,
  CollectionsApiService,
  CompanyBrand,
  MakeBuyoutRights,
  Tags,
  TagsApiService,
  TagsWithAssetCount,
  UserInfo,
} from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, AssetMetadata, BuyoutRightsMakeOS, BuyoutRightsMakeOSSecondLevel, MULTI_VALUE, MediaLists, TerritoryList } from '@thehubz/shared/model';
import { BehaviorSubject, Observable, Subject, Subscription, filter, map, tap } from 'rxjs';
import { AssetCommonService } from '../../services/asset-common.service';
import { availableBuyoutTitles, updateAvailableBuyoutTitles } from '../asset-buyout-rights/buyout.utils';
import { AssetRelationsFormControlComponent } from '../asset-detail-view/asset-relations-form-control/asset-relations-form-control.component';
import { AssetSettingsStepperConfigService } from '../asset-upload/stepper-config/asset-settings-stepper-config.service';
import {
  creatorDepartmentObj,
  getAddToExistingCollectionStepperConfig,
  territoriesFormArray,
} from '../asset-upload/stepper-config/asset-settings-stepper.constants';
import { AssetCustomControlComponent } from '../assets/asset-custom-control/asset-custom-control.component';
import { TagsChipComponent } from '../tags-dialog/tags-chip/tags-chip.component';
import { AssetStepperDialogFormService } from './asset-stepper-dialog-form.service';
import { AssetStepperDialogService } from './asset-stepper-dialog.service';
import {
  addSubLevelTypeToBuyoutRight,
  deepCloneControl,
  getTerritoriesToAdd as getCategoriesToAdd,
  getTerritoriesToRemove as getCategoriesToRemove,
  getControlPlaceholder,
  getFormFieldGroupByName,
  getRemovedValues,
  mapTerritoryAndMedia,
  mergeSecondLevelBuyoutRightsType,
  setControlPlaceholder,
  transformFormDataWithPlaceholders,
  updateFormFieldConfig,
  updateMultipleValuesInTerritories,
} from './asset-stepper-dialog.util';
import { FormArrayActionButtonComponent } from './form-array-action-button/form-array-action-button.component';

@Component({
  selector: 'thehubz-asset-settings-stepper',
  templateUrl: './asset-settings-stepper.component.html',
  styleUrls: ['./asset-settings-stepper.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDialogModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatRadioModule,
    MatCheckboxModule,
    MatDividerModule,
    MatAutocompleteModule,
    MatPaginatorModule,
    ClientCoreModule,
    UiKitModule,
    TagsChipComponent,
    AssetCustomControlComponent,
    AssetRelationsFormControlComponent,
    FormArrayActionButtonComponent,
  ],
})
export class AssetSettingsStepperComponent implements OnChanges, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(TagsChipComponent) tagsChipComponent!: TagsChipComponent;
  @ViewChild(ChipsGridRowComponent) tagsChipsGridRow!: ChipsGridRowComponent;
  @ViewChildren('sectionContainer') sectionContainers!: QueryList<ElementRef>;
  @ViewChild(MatSelectSearchComponent) matSelectSearchComp?: MatSelectSearchComponent;

  @ContentChild('reviewAndSubmit', { read: TemplateRef }) reviewAndSubmit!: TemplateRef<AnyType>;
  @ContentChild('shortBuyoutRightsSummary', { read: TemplateRef }) shortBuyoutRightsSummary!: TemplateRef<AnyType>;

  @Input() taggingOptions?: TaggingOptions;

  @Input() stepperModel!: {
    stepperData: StepperModel;
    additionalData?: AnyType;
    callFrom?: string;
    editable?: boolean;
    singleAssetUpdate?: boolean;
  };

  @Input() brandingKey!: CustomClients;
  @Input() assetLists: AssetUIModel[] = [];
  @Input() assetListFilesDraft: Asset[] = [];
  @Input() assetExistingFlag?: boolean = false;
  @Input() assetDuplicateFlag?: boolean = false;
  @Input() assetListFiles: AssetListFileObject[] = [];

  @Output() ready = new EventEmitter<boolean>();
  @Output() formSubmit = new EventEmitter<AnyType>();
  @Output() taggedUser = new EventEmitter<AnyType>();
  @Output() fieldChange = new EventEmitter<AnyType>();
  @Output() descriptionText = new EventEmitter<AnyType>();
  @Output() nameFieldChange = new EventEmitter<AnyType>();
  @Output() beforeStepChange = new EventEmitter<AnyType>();
  @Output() stepChange = new EventEmitter<StepperGroupModel>();
  @Output() deletedBuyoutRightIndex = new EventEmitter<number>();
  @Output() deletedBuyoutRightTerritoryIndex = new EventEmitter<{ parentRowIndex: number; territoryIndex: number }>();

  private deletedBuyoutRightIndex$ = new BehaviorSubject<number | null>(null);

  /**
   * Observable to emit when the component is destroyed.
   */
  private _destroyed$ = new Subject<void>();

  minLengthText = PROMPTS.TEXT.MIN_LENGTH;
  textIsRequired = PROMPTS.TEXT.IS_REQUIRED;
  textAreRequired = PROMPTS.TEXT.ARE_REQUIRED;

  tagPlaceHolderText = $localize`Search tags...`;
  textChipInputTooltip = $localize`:tooltip:Press \'Enter\' or \'Comma\' to add tags`;
  textNoTagsPermission = $localize`:no tags permission:User doesn\'t have access rights to create or edit tags`;

  /** Paginator object */
  paginatorObj: Partial<PageEvent> & Pick<PageEvent, 'pageIndex' | 'pageSize'> = {
    pageIndex: 0,
    pageSize: 15,
  };

  /** Store filter search text */
  searchText = '';
  stepperHeader!: string;
  currentUserID!: string;
  tagToHighlight!: string;
  currentUploadFileName = '';
  currentFormFieldName!: string;
  selectedBuyoutRightsType!: string;
  buyoutRightTitle?: string;
  selectedValueFromDropDown!: string;

  disableButtons = true;
  isEditorError = false;
  isInitialized = false;
  // set mat tree checkbox to true on load
  matTreeCheckbox = false;
  uploadInProgress = false;
  isMatTreeSelected = false;
  fileUploadRequired = false;
  formControlValidState = false;
  isLufthansaGroupClient = false;
  disableBuyoutRightsTitle = false;
  formControlSaveToTempValidState = false;
  showAssetUploadBuyoutRightsButton = false;

  tagData!: TagsDataConfig;
  /* File uploads and assets vars */
  files: FileObject[] = [];
  /** Auto complete tag data */
  autoCompTags: Tags[] = [];
  initialStepper!: StepperModel;
  allTagsListOption: Tags[] = [];
  assetDetails: AssetDetails[] = [];
  collectionsList: Collection[] = [];
  /** Store selected tag list */
  searchTagsList: TagsWithAssetCount[] = [];
  matTreeRawData: AspectRationFormatTree[] = [];
  stepperFormGroups: FormGroup = this._formBuilder.group({});
  buyoutRightsMakeOSSecondLevel = BuyoutRightsMakeOSSecondLevel;
  assetUploadBuyoutRights: UntypedFormArray = this._formBuilder.array([]);
  assetUploadBuyoutRightsEditMode = { editMode: false, editIndex: -1 };

  imgData: AnyType = {};
  assetUrlService: AnyType;
  aTopTerritories!: AnyType;
  filterObject: AnyType = {
    filters: {},
  };

  brandListMapping: FormArrayMapping = {};
  datePlaceHolder: FormArrayMapping = {};
  disableAddRow: FormArrayMapping = { 0: true };
  isCopiedFormArrayChecked: FormArrayMapping = {};
  selectedSecondLevelBuyoutRightsType: FormArrayMapping = {};

  /** Tags for chip display */
  tags: string[] = [];
  // list of user email from input mat-chip
  listUsers: string[] = [];
  collectionTags: string[] = [];
  selectedAgencyList: string[] = [];

  progress = 0;
  collectionNextStepIdx!: number;
  firstLevelFormArrayControlLength!: number;
  secondLevelFormArrayControlLength!: number;

  tagSub?: Subscription;
  keywordSub?: Subscription;
  formControlSub?: Subscription;

  AnyAsset = anyAsset;

  formFieldVisibility: Record<string, boolean[]> = {};

  /** Number of items to display on a page */
  dataSource!: MatTableDataSource<TagsWithAssetCount>;

  /** Connect with the MatTableDataSource */
  tagsObs!: Observable<TagsWithAssetCount[]>;

  CustomClients = CustomClients;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AnyType,
    @Inject(ENVIRONMENT) public environment: ClientEnvironment,
    private el: ElementRef,
    private dialog: MatDialog,
    private _formBuilder: FormBuilder,
    private optionsLabel: OptionsLabelPipe,
    private tagsApiService: TagsApiService,
    private assetApiService: AssetsApiService,
    private assetTagsService: AssetTagsService,
    private commonUtilityLib: CommonUtilityLib,
    private confirmDialog: ConfirmDialogService,
    private changeDetectorRef: ChangeDetectorRef,
    public assetCommonService: AssetCommonService,
    private formService: AssetStepperDialogFormService,
    private collectionAPIService: CollectionsApiService,
    private coreAssetsTagsService: CoreAssetsTagsService,
    private stepperDialogService: AssetStepperDialogService,
    public dialogRef: MatDialogRef<AssetSettingsStepperComponent>,
    private assetSettingsStepperConfigService: AssetSettingsStepperConfigService,
  ) {
    this.currentUserID = assetCommonService.currentUser.userData._id;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes[AssetMetadata.Others.STEPPER_MODEL] && changes[AssetMetadata.Others.STEPPER_MODEL].currentValue && !this.isInitialized) {
      this.init();
    }

    // on no ...
    this.changeDetectorRef.detectChanges();
  }

  ngAfterViewInit(): void {
    // do we need that already here?
    this.fetchAllCollections();
  }

  private init(): void {
    this.initialStepper = JSON.parse(JSON.stringify(this.stepperModel.stepperData));
    this.assetUrlService = this.dialogRef.componentInstance.assetUrlService;
    this.stepperDialogService.resetAllSteps(this.initialStepper, 0);
    const stepperInfo = Object.assign({}, ...this.initialStepper.stepper.filter((step: StepperGroupModel) => step.selected));
    this.stepperHeader = !stepperInfo.hideHeader ? stepperInfo.stepperName : '';
    this.initStepperFormGroups();
    this.isInitialized = true;

    // Timeout is necessary for the parent components ViewChild properly to have access to the stepper
    setTimeout(() => {
      this.ready.emit(true);
    }, 0);

    if ([CustomClients.LufthansaMediacenter, CustomClients.LufthansaMedialounge].includes(this.brandingKey)) {
      this.textChipInputTooltip = '';
    }
    this.tagData = {
      filters: this.filterObject.filters,
      callFrom: 'child',
    };
    if (this.stepperFormGroups.controls[AssetMetadata.General.TAGS] && this.brandingKey !== CustomClients.VKB) {
      this.tagSub = this.assetTagsService
        .fetchTagsForAutoComp(this.stepperFormGroups.controls[AssetMetadata.General.TAGS].get(AssetMetadata.General.TAGS))
        ?.pipe(
          tap((data) => {
            this.autoCompTags = data;
            this.tagToHighlight = this.assetTagsService.accessTagName();
          }),
        )
        .subscribe();
    }
    if (this.stepperFormGroups.controls[AssetMetadata.General.TAGS] && this.brandingKey === CustomClients.VKB) {
      this.tagsApiService.tagsControllerFindAll().subscribe((data) => {
        if (data) this.allTagsListOption = data;
      });
    }
    const clientID = this.assetLists[0]?.client?._id as unknown as string;
    this.isLufthansaGroupClient = clientID === LufthansaGroupID ? true : false;
    if (this.stepperModel.editable && this.stepperModel.callFrom === AssetMetadata.Rights.ASSET_USAGE_RIGHT) {
      if (this.brandingKey === CustomClients.Standard) {
        this.patchMakeOsBuyoutRights();
      } else {
        this.patchUsageRights();
      }
    }
  }

  private patchMakeOsBuyoutRights(patchUploadStepperBuyoutRights?: MakeBuyoutRights) {
    const assetMakeBuyoutRights = patchUploadStepperBuyoutRights || this.assetLists[0].makeBuyoutRights?.[this.stepperModel.additionalData.usageRightIndex];
    const stepper = this.initialStepper.stepper;
    for (const buyoutRightsProp in assetMakeBuyoutRights) {
      const buyoutRightsPropValue = assetMakeBuyoutRights[buyoutRightsProp as keyof typeof assetMakeBuyoutRights];
      switch (buyoutRightsProp) {
        case AssetMetadata.Rights.BUYOUT_RIGHTS_TITLE: {
          this.buyoutRightTitle = typeof buyoutRightsPropValue === 'string' ? buyoutRightsPropValue : undefined;
          this.patchBuyoutRightsTitle(buyoutRightsProp, buyoutRightsPropValue, stepper);
          break;
        }
        case AssetMetadata.Rights.BUYOUT_RIGHTS: {
          const buyoutRightsFields = buyoutRightsPropValue as BuyoutRightFields[];
          const usageRightsStep = this.assetSettingsStepperConfigService.findStepper(
            stepper,
            AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION,
          ) as StepperGroupModel;
          this.patchBuyoutRightsFields(buyoutRightsFields, usageRightsStep);
          break;
        }
      }
    }
  }

  private patchBuyoutRightsFields(fields: BuyoutRightFields[], usageRightsStep: StepperGroupModel): void {
    if (!fields?.length) return;

    let buyoutRightsFormArrayFields: FormControlModel[] = [];

    fields.forEach((field, fieldIndex) => {
      if (field.subLevelType) {
        this.selectedValueFromDropDown = field.subLevelType;
        this.processBuyoutRightsSecondLevelType(field.subLevelType, [], usageRightsStep);
      }

      if (usageRightsStep.formFields) {
        const buyoutRightsFormArrayIndex = usageRightsStep.formFields.findIndex(
          (formField) => formField.formFieldName === AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY,
        );
        if (buyoutRightsFormArrayIndex !== undefined && buyoutRightsFormArrayIndex !== -1) {
          buyoutRightsFormArrayFields = usageRightsStep.formFields[buyoutRightsFormArrayIndex].formFieldGroup as FormControlModel[];
        }
      }

      const control = this.ensureBuyoutRightsFormControl(fieldIndex, !!field.subLevelType);
      if (!control) return;

      for (const prop in field) {
        const value = field[prop as keyof BuyoutRightFields] || '';
        this.patchBuyoutRightFieldProp(control, prop, value, field, buyoutRightsFormArrayFields);
      }
    });
  }

  private patchBuyoutRightFieldProp(
    control: AbstractControl,
    prop: string,
    value: AnyType,
    fullField: BuyoutRightFields,
    buyoutRightsFormArrayFields: FormControlModel[],
  ): void {
    const propControl = control.get(prop);
    if (prop === AssetMetadata.Others.TERRITORIES) {
      fullField.territories?.forEach((territoryItem, territoryIndex) => {
        const territoryFormArray = control.get(AssetMetadata.Form.TERRITORIES_FORM_ARRAY) as UntypedFormArray;
        const mapped = mapTerritoryAndMedia(territoryItem);

        if (!territoryFormArray?.at(territoryIndex)) {
          this.addRow(
            territoryIndex,
            control,
            AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION,
            AssetMetadata.Form.TERRITORIES_FORM_ARRAY,
            territoriesFormArray(),
          );
        }
        const territoriesFormArrayFields = getFormFieldGroupByName(buyoutRightsFormArrayFields, AssetMetadata.Form.TERRITORIES_FORM_ARRAY);

        this.disableAddRow[territoryIndex] = false;

        updateMultipleValuesInTerritories(mapped, territoryFormArray?.at(territoryIndex), territoriesFormArrayFields);

        if (territoryItem.noExpiry) {
          this.updateExpiryDateControlOnNoExpiry(territoryFormArray?.at(territoryIndex));
        }
      });
    } else {
      if (!propControl) return;
      if (value === MULTI_VALUE) {
        propControl.patchValue(null);
        propControl.removeValidators([Validators.required]);
        propControl.updateValueAndValidity();
        setControlPlaceholder(propControl, PROMPTS.TEXT.MULTI_VALUE);
      } else {
        propControl.patchValue(value);
        setControlPlaceholder(propControl, buyoutRightsFormArrayFields?.find((f) => f.formFieldName === prop)?.placeholder || '');
      }
    }
  }

  getPlaceholder(control: AbstractControl, defaultPlaceholder: string) {
    return getControlPlaceholder(control, defaultPlaceholder);
  }

  get reviewData() {
    return this.stepperModel.singleAssetUpdate
      ? this.stepperFormGroups.getRawValue()
      : transformFormDataWithPlaceholders(this.stepperFormGroups.getRawValue(), this.stepperFormGroups);
  }

  private ensureBuyoutRightsFormControl(index: number, skipAddRow = false): AbstractControl | null {
    const groupControl = this.stepperFormGroups.controls[AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION];
    const arrayControl = groupControl?.get(AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY) as UntypedFormArray;
    let controlAtIndex = arrayControl?.at(index);

    if (!controlAtIndex && !skipAddRow) {
      const fieldConfig = this.formService.updateFormFieldsVisibleProp(this.buyoutRightTitle || '');
      this.addRow(
        index,
        groupControl,
        AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION,
        AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY,
        fieldConfig[0]?.formFieldGroup || [],
      );
      controlAtIndex = arrayControl?.at(index);
    }

    return controlAtIndex || null;
  }

  /**
   * Patch usage right
   */
  private patchUsageRights() {
    // Store asset usage right object
    const assetUsageRight = this.assetLists[0].usageRights?.[this.stepperModel.additionalData.usageRightIndex];
    const stepper = this.initialStepper.stepper;
    // Loop through asset usage right object
    for (const usageProp in assetUsageRight) {
      // Store asset usage right property value
      const buyoutRightsPropValue = assetUsageRight[usageProp as keyof typeof assetUsageRight];
      // Handle specific usage right properties
      switch (usageProp) {
        case AssetMetadata.Rights.BUYOUT_RIGHTS_TITLE: {
          this.patchBuyoutRightsTitle(usageProp, buyoutRightsPropValue, stepper);
          break;
        }
        case AssetMetadata.Others.IMAGE_SOURCE: {
          const usageRightsGeneralInformation = this.assetSettingsStepperConfigService.findStepper(
            stepper,
            AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION,
          ) as StepperGroupModel;
          const generalInfoFormFields = usageRightsGeneralInformation?.formFields || [];
          const buyoutRightsTitleForm = generalInfoFormFields.find((form) => form.formFieldName === usageProp) || ({} as FormControlModel);
          const buyoutRightsFormValue = buyoutRightsTitleForm.options?.find((opt) => opt._id === buyoutRightsPropValue);
          this.onValueChange({ source: {} as AnyType, value: buyoutRightsFormValue }, usageProp, buyoutRightsTitleForm, usageRightsGeneralInformation);
          if (this.stepperFormGroups.controls[AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION]) {
            this.stepperFormGroups.controls[AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION].get(usageProp)?.patchValue(buyoutRightsFormValue);
          }
          break;
        }
        case AssetMetadata.Rights.BUYOUT_RIGHTS: {
          const buyoutRights = buyoutRightsPropValue as BuyoutRights[];
          // Get BuyoutRights stepper object from initialStepper.stepperData
          const usageRights = this.assetSettingsStepperConfigService.findStepperForm(stepper, AssetMetadata.Rights.BUYOUT_RIGHTS) || [];
          // Get form field group
          const formFieldGroup: FormControlModel[] = usageRights.map((form) => form.formFieldGroup).flat(2) as NonNullable<FormControlModel[]>;
          // removing 1st formArray formControl as it is created by default by createStepperFormGroups method
          if (this.stepperFormGroups.controls[AssetMetadata.Rights.BUYOUT_RIGHTS] && assetUsageRight.buyoutRights?.length) {
            (
              this.stepperFormGroups.controls[AssetMetadata.Rights.BUYOUT_RIGHTS].get(AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY) as UntypedFormArray
            ).removeAt(0);
            formFieldGroup?.forEach((fieldGrp) => {
              this.formFieldVisibility[fieldGrp.formFieldName].splice(0, 1);
            });
          }
          if (buyoutRights.length) {
            // Loop through buyout rights and create form array controls
            buyoutRights.forEach((buyoutRight, i) => {
              // Call addRow to add formArray
              this.addRow(
                i,
                this.stepperFormGroups.controls[AssetMetadata.Rights.BUYOUT_RIGHTS],
                AssetMetadata.Rights.BUYOUT_RIGHTS,
                AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY,
                formFieldGroup,
              );
              // Patch buyout right properties by looping through buyoutRight
              for (const right in buyoutRight) {
                // Get the value of the current property in buyoutRight.
                const buyoutRightPropValue = buyoutRight[right as keyof typeof buyoutRight] || '';
                // Find the corresponding value in the formFieldGroup.
                const buyoutRightOptionValue = formFieldGroup
                  .find((field) => field.formFieldName === right)
                  ?.options?.find((opt) => opt._id === buyoutRightPropValue);
                // Get the control of the current property in the form array.
                const control = (
                  this.stepperFormGroups.controls[AssetMetadata.Rights.BUYOUT_RIGHTS].get(AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY) as UntypedFormArray
                ).at(i);
                if (buyoutRightPropValue === MULTI_VALUE) {
                  control.get(right)?.patchValue(null);
                  control.get(right)?.removeValidators([Validators.required]);
                  control.get(right)?.updateValueAndValidity();
                  this.datePlaceHolder[i] = PROMPTS.TEXT.MULTI_VALUE;
                } else {
                  // Patch the value of the current property in the form array.
                  control.get(right)?.patchValue(buyoutRightOptionValue ?? buyoutRightPropValue);
                }
                // If the current property is EXPIRY_DATE and buyoutRightPropValue (NO_EXPIRY) is truthy,
                // disable and update the value and validity of the EXPIRY_DATE property.
                if (right === AssetMetadata.Rights.EXPIRY_DATE && !buyoutRightPropValue) {
                  this.updateExpiryDateControlOnNoExpiry(control);
                }
              }
              this.disableAddRow[i] = false;
            });
          }
          break;
        }
        case AssetMetadata.Others.CREATOR_DEPARTMENT: {
          if (buyoutRightsPropValue === MULTI_VALUE) {
            this.multipleValuesUpdates(stepper, usageProp);
          } else {
            const optionValue = creatorDepartmentObj(this.brandingKey).find(({ _id }) => _id === buyoutRightsPropValue);
            this.stepperFormGroups.controls[AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION].get(usageProp)?.patchValue(optionValue);
          }
          break;
        }
        default: {
          if (buyoutRightsPropValue === MULTI_VALUE || (buyoutRightsPropValue && buyoutRightsPropValue[0] === MULTI_VALUE)) {
            this.multipleValuesUpdates(stepper, usageProp);
          } else {
            // Patch rest of the fields
            this.stepperFormGroups.controls[AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION].get(usageProp)?.patchValue(buyoutRightsPropValue);
          }
          break;
        }
      }
    }
  }

  private updateExpiryDateControlOnNoExpiry(control: AbstractControl) {
    control.get(AssetMetadata.Rights.EXPIRY_DATE)?.disable();
    control.get(AssetMetadata.Rights.NO_EXPIRY)?.setValue(true);
    control.get(AssetMetadata.Rights.EXPIRY_DATE)?.updateValueAndValidity();
  }

  private patchBuyoutRightsTitle(
    propertyKey: string,
    propertyValue: string | string[] | BuyoutRights[] | BuyoutRightFields[] | undefined,
    stepper: StepperGroupModel[],
  ) {
    // Get General Information stepper object from initialStepper.stepperData
    const usageRightsGeneralInformation = this.assetSettingsStepperConfigService.findStepper(
      stepper,
      AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION,
    ) as StepperGroupModel;
    // Get General Information form fields from usage rights general information stepper
    const generalInfoFormFields = usageRightsGeneralInformation?.formFields || [];
    // Get buyout rights title form field
    const buyoutRightsTitleForm = generalInfoFormFields.find((form) => form.formFieldName === propertyKey) || ({} as FormControlModel);
    // Find and set the selected buyout rights title option
    const buyoutRightsFormValue = buyoutRightsTitleForm.options?.find((opt) => opt._id === propertyValue);
    // disable buyoutRightsTitle on patching
    this.disableBuyoutRightsTitle = true;
    // Call onValueChange to build buyout right fields
    this.onValueChange({ source: {} as AnyType, value: buyoutRightsFormValue }, propertyKey, buyoutRightsTitleForm, usageRightsGeneralInformation);
    // Patch value of buyoutRightsTitle
    if (this.stepperFormGroups.controls[AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION]) {
      setTimeout(() => {
        // reset set touched and dirty form properties to false
        this.stepperFormGroups.controls[AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION].get(propertyKey)?.reset(buyoutRightsFormValue);
      });
    }
  }

  multipleValuesUpdates(stepper: StepperGroupModel[], usageProp: string) {
    const genInfoIndex = stepper.findIndex((step) => step.stepperGroupName === AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION);
    if (genInfoIndex !== -1) {
      const formFields = stepper[genInfoIndex].formFields;
      const formFieldsIndex = formFields?.findIndex((field) => field.formFieldName === usageProp) as number;
      if (formFieldsIndex !== -1 && formFields) {
        this.stepperFormGroups.controls[AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION].get(usageProp)?.patchValue(null);
        formFields[formFieldsIndex].placeholder = PROMPTS.TEXT.MULTI_VALUE;
        this.stepperFormGroups.controls[AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION].get(usageProp)?.removeValidators([Validators.required]);
      }
    }
  }

  private initStepperFormGroups() {
    this.stepperFormGroups = new FormGroup({});
    if (this.initialStepper) {
      const stepperFormGroups = this.formService.createStepperFormGroups(this.initialStepper.stepper, this.formFieldVisibility);
      this.stepperFormGroups = stepperFormGroups.stepperFormGroups;
      this.tags = stepperFormGroups.tagsArray;
    }
  }

  /**
   * This function is used for formArray. Mainly for the milestone. It will return the form control based on field name
   * @param fieldArrayName
   * @param formGroupName
   * @returns
   */
  getFormArrayControls(formArrayControls: AbstractControl | null, fieldArrayName: string, level: string) {
    if (formArrayControls) {
      const formArray = formArrayControls.get(fieldArrayName) as UntypedFormArray;
      if (formArray) {
        if (level === 'first') this.firstLevelFormArrayControlLength = formArray.controls.length;
        if (level === 'second') this.secondLevelFormArrayControlLength = formArray.controls.length;
        return formArray.controls;
      }
    }
    return [];
  }

  onValueChange(event: MatSelectChange, formFieldName: string, formField: FormControlModel, formGroupField: StepperGroupModel) {
    this.selectedValueFromDropDown = event.value._id;
    if (formGroupField?.stepperGroupName === AssetMetadata.Collections.COLLECTION && formFieldName === AssetMetadata.Others.SELECT_TYPE) {
      this.handleCollectionSelection(event, formGroupField);
    }

    if (formFieldName === 'client') {
      this.isLufthansaGroupClient = this.selectedValueFromDropDown === LufthansaGroupID ? true : false;
    }

    if (formFieldName === AssetMetadata.Rights.BUYOUT_RIGHTS_TITLE || formFieldName === AssetMetadata.Rights.BUYOUT_RIGHTS_SECOND_LEVEL_TYPE) {
      this.handleBuyoutRightsSelection(event, formFieldName, formGroupField, this.selectedValueFromDropDown);

      // Update the visibility of the "Add multiple buyout right" button based on the selected value
      this.showAssetUploadBuyoutRightsButton = !event.value.secondLvl && !!event.value;

      // Reset the copied form array check state when territories form array value changes
      if (this.stepperFormGroups.controls[AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION].get(AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY)) {
        const buyoutRightsFormArray = this.stepperFormGroups.controls[AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION].get(
          AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY,
        ) as UntypedFormArray;
        buyoutRightsFormArray.controls.forEach((_, index) => {
          const territoryControl = buyoutRightsFormArray.controls.at(index)?.get(AssetMetadata.Form.TERRITORIES_FORM_ARRAY) as UntypedFormArray;
          territoryControl.valueChanges.subscribe(() => {
            this.isCopiedFormArrayChecked[index + 1] = false;
          });
        });
      }
    }

    if (this.brandingKey === CustomClients.Standard && formFieldName !== AssetMetadata.Rights.BUYOUT_RIGHTS_TITLE) this.updateTerritoryOptions();

    if (formFieldName === AssetMetadata.Others.IMAGE_SOURCE && this.brandingKey === CustomClients.SEAT) {
      this.handleImageSourceSelection(event, formGroupField);
    }
    /**
     * Used for tags filter
     */
    this.handleTagFilters(event, formFieldName);
    /***************************/
    this.fieldChange.emit({ event, formFieldName, formField, formGroupField, initialStepper: this.initialStepper });
  }

  private updateTerritoryOptions(): void {
    const stepper = this.initialStepper.stepper;
    // Find the stepper for usage rights
    const usageRightStepperFormFields = this.assetSettingsStepperConfigService.findStepper(stepper, AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION);
    if (!usageRightStepperFormFields) return;

    // Find the buyout rights form array inside it
    const buyoutRightsFormFields = this.findFormFieldByName(AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY, usageRightStepperFormFields.formFields);
    if (!buyoutRightsFormFields) return;

    // Find the territories form array inside buyout rights
    const territoriesFormFields = this.findFormFieldByName(AssetMetadata.Form.TERRITORIES_FORM_ARRAY, buyoutRightsFormFields.formFieldGroup);
    if (!territoriesFormFields) return;

    // update territories form group
    territoriesFormFields.formFieldGroup = territoriesFormArray(this.isLufthansaGroupClient);
  }

  private findFormFieldByName(fieldName: string, formFields: FormControlModel[] = []) {
    return formFields.find((formField) => formField.formFieldName === fieldName);
  }

  private handleCollectionSelection(event: MatSelectChange, formGroupField: AnyType) {
    const uploadAssetAction = formGroupField.actions?.find((action: ActionsModel) => action.actionType === 'uploadAsset');
    const nextAssetAction = formGroupField.actions?.find((action: ActionsModel) => action.actionType === AssetMetadata.Collections.COLLECTION);

    this.collectionNextStepIdx = formGroupField.stepperId;

    if (event.value && uploadAssetAction && nextAssetAction) {
      uploadAssetAction.visible = false;
      nextAssetAction.visible = true;
      setTimeout(() => this.nextGoToStep(nextAssetAction, this.stepperFormGroups.controls[AssetMetadata.Collections.COLLECTION]), 100);
    } else {
      this.removeCollectionMetadata();
      if (this.collectionNextStepIdx) this.initialStepper.stepper.splice(this.collectionNextStepIdx, 1);
      if (uploadAssetAction) uploadAssetAction.visible = true;
      if (nextAssetAction) nextAssetAction.visible = false;
    }
  }

  private removeCollectionMetadata() {
    [AssetMetadata.Collections.CREATE_NEW, AssetMetadata.Collections.ADD_TO_EXISTING].forEach((item) => this.stepperFormGroups.removeControl(item));
  }

  private handleBuyoutRightsSelection(event: MatSelectChange, formFieldName: string, formGroupField: StepperGroupModel, selectedValue: string) {
    const availableTitles = availableBuyoutTitles(this.assetLists[0]);

    if (this.shouldUpdateBuyoutTitles(availableTitles)) {
      updateAvailableBuyoutTitles(availableTitles, this.assetLists[0].usageRights ?? [], this.stepperModel.additionalData.usageRightIndex);
    }

    if (formFieldName === AssetMetadata.Rights.BUYOUT_RIGHTS_TITLE) {
      this.processBuyoutRightsTitle(event, selectedValue, formGroupField, availableTitles);
    } else if (formFieldName === AssetMetadata.Rights.BUYOUT_RIGHTS_SECOND_LEVEL_TYPE && event.value) {
      this.processBuyoutRightsSecondLevelType(selectedValue, availableTitles, formGroupField);
    }
  }

  private shouldUpdateBuyoutTitles(availableTitles: string[]): boolean {
    return (
      !!this.stepperModel.editable &&
      !!availableTitles.length &&
      !!this.assetLists[0].usageRights?.length &&
      this.stepperModel.additionalData.usageRightIndex !== undefined
    );
  }

  private processBuyoutRightsTitle(event: MatSelectChange, selectedValue: string, formGroupField: StepperGroupModel, availableTitles: string[]) {
    if (this.brandingKey !== CustomClients.Standard) {
      this.formService.buildBuyoutRightFields(event, formGroupField, this.stepperFormGroups, availableTitles, this.disableBuyoutRightsTitle);
    } else {
      this.selectedSecondLevelBuyoutRightsType = {};
      this.selectedBuyoutRightsType = this.optionsLabel.transform(selectedValue, BuyoutRightsMakeOS);
      this.formService.buildBuyoutRightSecondLevelFields(
        event,
        formGroupField,
        this.stepperFormGroups,
        availableTitles,
        this.disableBuyoutRightsTitle,
        this.formFieldVisibility,
        this.isLufthansaGroupClient,
      );
    }
  }

  private processBuyoutRightsSecondLevelType(selectedValue: string, availableTitles: string[], formGroupField: StepperGroupModel) {
    this.selectedBuyoutRightsType = '';
    const buyoutRightsFormArray = this.stepperFormGroups.controls[AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION].get(
      AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY,
    ) as UntypedFormArray;

    const buyoutRightsFormArrayLength = buyoutRightsFormArray?.length || 0;

    if (buyoutRightsFormArrayLength >= 1) {
      const buyoutRightFields = this.formService.updateFormFieldsVisibleProp(selectedValue);
      const formFieldGroup = buyoutRightFields[0]?.formFieldGroup || [];
      this.addNewFieldGroup(
        buyoutRightsFormArrayLength,
        this.stepperFormGroups.controls[AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION],
        AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY,
        formFieldGroup,
      );
    } else {
      this.formService.buildBuyoutRightSecondLevelMakeOsFields(
        selectedValue,
        formGroupField,
        this.stepperFormGroups,
        availableTitles,
        this.disableBuyoutRightsTitle,
        this.formFieldVisibility,
        this.isLufthansaGroupClient,
      );
    }

    this.selectedSecondLevelBuyoutRightsType[buyoutRightsFormArrayLength] = selectedValue;

    setTimeout(() => {
      this.stepperFormGroups.controls[AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION]
        .get(AssetMetadata.Rights.BUYOUT_RIGHTS_SECOND_LEVEL_TYPE)
        ?.setValue(null);
      this.stepperFormGroups.controls[AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION]
        .get(AssetMetadata.Rights.BUYOUT_RIGHTS_SECOND_LEVEL_TYPE)
        ?.removeValidators([Validators.required]);
      this.stepperFormGroups.controls[AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION]
        .get(AssetMetadata.Rights.BUYOUT_RIGHTS_SECOND_LEVEL_TYPE)
        ?.updateValueAndValidity();
      if (!this.stepperModel.editable) this.scrollToBottom();
    }, 100);
  }

  private handleImageSourceSelection(event: MatSelectChange, formGroupField: AnyType) {
    const additionalInformationField = formGroupField.formFields.find(
      (formField: FormControlModel) => formField.formFieldName === AssetMetadata.Others.ADDITIONAL_INFORMATION,
    );

    if (additionalInformationField) {
      additionalInformationField.visible = event.value?._id === AssetMetadata.General.OTHER;
    }
  }

  private handleTagFilters(event: MatSelectChange, formFieldName: string) {
    const filterName = event.value?.name;
    const filterMapping: { [key: string]: string } = {
      agency: 'agency.name',
      client: 'client.name',
      brand: 'brand.name',
    };

    if (filterMapping[formFieldName]) {
      this.filterObject.filters = {
        ...this.filterObject.filters,
        [filterMapping[formFieldName]]: filterName,
      };
    }
  }

  firstLevelValueChange(
    event: MatSelectChange,
    rowIndex: number,
    formFieldName: string,
    formField: FormControlModel,
    formGroupField: StepperGroupModel,
    formArrayFieldName?: string,
  ) {
    const selectedValue: string = event.value._id;
    this.currentFormFieldName = formFieldName;
    if (this.brandingKey === CustomClients.Vorwerk) {
      switch (formFieldName) {
        case AssetMetadata.General.CLIENT: {
          const brand: Pick<CompanyBrand, '_id' | 'name'>[] = event.value.brands;
          const brandFormField = formField.formFieldGroup?.find((field) => field.formFieldName === AssetMetadata.General.BRAND);
          if (brandFormField) this.brandListMapping[rowIndex] = brand;
          break;
        }
        case AssetMetadata.General.BRAND: {
          if (event.value) {
            this.formFieldVisibility[AssetMetadata.Product.CATEGORY][rowIndex] = true;
          } else {
            this.formFieldVisibility[AssetMetadata.Product.CATEGORY][rowIndex] = false;
            this.formFieldVisibility[AssetMetadata.Product.PRODUCT][rowIndex] = false;
            this.formFieldVisibility[AssetMetadata.Product.SUBCATEGORY][rowIndex] = false;
          }
          break;
        }
        case AssetMetadata.Product.CATEGORY: {
          if (event.value && selectedValue === AssetMetadata.Product.PRODUCT) {
            this.formFieldVisibility[AssetMetadata.Product.PRODUCT][rowIndex] = true;
          } else {
            this.formFieldVisibility[AssetMetadata.Product.PRODUCT][rowIndex] = false;
            this.formFieldVisibility[AssetMetadata.Product.SUBCATEGORY][rowIndex] = false;
          }
          break;
        }
        case AssetMetadata.Product.PRODUCT: {
          if (event.value) this.formFieldVisibility[AssetMetadata.Product.SUBCATEGORY][rowIndex] = true;
          else this.formFieldVisibility[AssetMetadata.Product.SUBCATEGORY][rowIndex] = false;
          break;
        }
      }
    }
    if (this.brandingKey === CustomClients.SEAT && formFieldName === AssetMetadata.Rights.BUYOUT_RIGHTS_TITLE) {
      setTimeout(() => {
        this.adjustBuyoutRightOptions(formGroupField?.stepperGroupName || '', formArrayFieldName || '', formField.formFieldGroup);
      }, 0);
    }
    setTimeout(() => {
      this.changeDisableAddRowButton(rowIndex, formField.formFieldName, this.stepperFormGroups.controls[formGroupField?.stepperGroupName || '']);
    }, 0);
    this.fieldChange.emit({
      rowIndex,
      event,
      formFieldName,
      formField,
      formGroupField: { formFields: formField.formFieldGroup, stepperGroupName: formGroupField?.stepperGroupName },
      initialStepper: this.initialStepper,
    });
  }

  secondLevelValueChange(event: MatSelectChange, secondLevelRowIndex: number, formFieldName: string, secondLevelFormArrayControls: AbstractControl) {
    const formControl = secondLevelFormArrayControls.get(formFieldName);
    if (!formControl) return;

    this.currentFormFieldName = formFieldName;

    const selectedValues: AnyType[] = event.value;
    const territories = TerritoryList;
    const oldFormValue: AnyType[] = formControl?.getRawValue() || [];

    if (this.brandingKey === CustomClients.Standard) {
      setTimeout(() => {
        const updatedFormValue: AnyType[] = formControl?.getRawValue() || [];
        const removedFormValue = getRemovedValues(oldFormValue, selectedValues);
        if ([AssetMetadata.Others.TERRITORY, AssetMetadata.General.MEDIA].includes(formFieldName)) {
          const quickSelectionMap: Record<
            string,
            {
              _id: string;
              name: string;
              categories: string[];
            }[]
          > = {
            [AssetMetadata.Others.TERRITORY]: territories,
            [AssetMetadata.General.MEDIA]: MediaLists,
          };

          const quickSelectionList = quickSelectionMap[formFieldName] ?? [];

          let filteredList: { _id: string; name: string; categories?: string[] }[] = [];

          if (removedFormValue.length) {
            filteredList = getCategoriesToRemove(quickSelectionList, removedFormValue);
            formControl?.patchValue(updatedFormValue.filter((value) => !filteredList.some((territory) => territory._id === value._id)));
          } else {
            filteredList = getCategoriesToAdd(quickSelectionList, selectedValues);
            const uniqueValues = [...new Map([...updatedFormValue, ...filteredList].map((item) => [item._id, item])).values()];
            formControl?.patchValue(uniqueValues);
          }
        }
      }, 0);
    }

    setTimeout(() => {
      const stepperFormGroupsControls = secondLevelFormArrayControls?.value;
      const isEmpty = Object.values(stepperFormGroupsControls).every(
        (x: AnyType) => x === null || x === '' || x === undefined || x === false || x.length === 0,
      );
      this.disableAddRow[secondLevelRowIndex] = isEmpty;
    }, 0);
  }

  checkFromArrayValue(i: number, formArrayControls: AbstractControl, formArrayFieldName: string, formFieldName: string) {
    const currentFormArray = formArrayControls.get(formArrayFieldName) as UntypedFormArray;
    if (currentFormArray.at(i).errors?.[AssetMetadata.Others.DUPLICATE] && currentFormArray.at(i).get(formFieldName)?.value) return true;
    else return false;
  }

  checkBuyoutRightsTitleRequiredValue(
    i: number,
    formFields: AnyType,
    stepperGroupName: string,
    formArrayFieldName: string,
    formFieldName: string,
    currentFormFieldName: string,
    currentFieldLabel: string,
  ) {
    const currentFormArray = this.stepperFormGroups.controls[stepperGroupName].get(formArrayFieldName) as UntypedFormArray;
    const buyoutRightsTitleValue = currentFormArray.at(i).get(formFieldName)?.value?._id;
    const currentFieldControl = currentFormArray.at(i).get(currentFormFieldName);
    if (buyoutRightsTitleValue && formFields?.formFieldGroup?.length) {
      const currentFieldIndex = formFields.formFieldGroup.findIndex((field: FormControlModel) => {
        if (!currentFormFieldName) {
          return field.fieldLabel === currentFieldLabel;
        }
        return field.formFieldName === currentFormFieldName;
      });
      if (currentFieldIndex > -1) {
        if (formFields.formFieldGroup[currentFieldIndex]?.linkableArray?.includes(buyoutRightsTitleValue)) {
          return true;
        } else {
          currentFieldControl?.removeValidators([Validators.required]);
          currentFieldControl?.updateValueAndValidity();
          return false;
        }
      }
    }
    return false;
  }

  changeDisableAddRowButton(rowIndex: number, formFieldName?: string, formArrayControls?: AbstractControl) {
    const stepperFormGroupsControls = formArrayControls?.value;
    const isEmpty = Object.values(stepperFormGroupsControls[formFieldName || ''][rowIndex]).every(
      (x) => x === null || x === '' || x === undefined || x === false,
    );
    this.disableAddRow[rowIndex] = isEmpty;
  }

  onDateSelection(event: MatDatepickerInputEvent<AnyType, AnyType>, formField: FormControlModel, i: number) {
    this.disableAddRow[i] = false;
    this.fieldChange.emit({ event, formFieldName: formField.formFieldName, formField });
  }

  onInput(
    event: AnyType,
    formField: FormControlModel,
    i: number,
    groupName: string,
    formArrayControls?: AbstractControl<AnyType, AnyType>,
    controlName?: string,
    formArrayFlag?: boolean,
  ) {
    const input = event.target.value;
    // Check if input is in numeric form (e.g., 24082024)
    if (/^\d{8}$/.test(input)) {
      const day = input.substring(0, 2);
      const month = input.substring(2, 4);
      const year = input.substring(4, 8);

      // Format the date as DD/MM/YYYY
      const formattedDate = `${month}/${day}/${year}`;

      // Update the dateValue bound to the input
      const newDate = new Date(formattedDate);
      event.value = newDate;
      if (formArrayFlag && controlName) {
        formArrayControls?.get(controlName)?.patchValue(newDate);
      } else {
        this.stepperFormGroups.controls[groupName].get(formField['formFieldName'])?.patchValue(newDate);
      }

      this.onDateSelection(event, formField, i);
    }
  }

  compareMatSelect(list: AnyType, selected: AnyType): boolean {
    return list && selected ? list._id === selected._id && list.id === selected.id : list === selected;
  }

  closeModal() {
    if (!this.stepperFormGroups.pristine) {
      this.dialog
        .open(ConfirmDialogComponent, {
          disableClose: true,
          data: {
            title: $localize`Confirm`,
            message: $localize`Are you sure you want to close? All unsaved data will be lost`,
            confirmCaption: PROMPTS.LABELS.YES,
            cancelCaption: PROMPTS.LABELS.NO,
          },
        })
        .afterClosed()
        .subscribe((data) => {
          if (data) this.dialogRef.close({ responseData: this.assetLists });
        });
    } else {
      this.dialogRef.close({ responseData: this.assetLists });
    }
  }

  addNewFieldGroup(i: number, formArrayControls: AbstractControl, formFieldName: string, formFieldGroup: FormControlModel[]) {
    const currentFormArray = formArrayControls.get(formFieldName) as UntypedFormArray;
    currentFormArray.push(this.formService.buildFormGroup(formFieldGroup));
    formFieldGroup?.forEach((fieldGrp) => {
      if (fieldGrp.fieldType !== AssetMetadata.Form.FORM_ARRAY) this.formFieldVisibility[fieldGrp.formFieldName].splice(i + 1, 0, fieldGrp.visible);
      this.formService.getFormGroupArray(this.selectedValueFromDropDown, currentFormArray, i);
    });
  }

  /**
   * Add controls to form array
   * @param formFieldName
   * @param stepperGroupName
   * @param formFieldGroup
   */
  addRow(i: number, formArrayControls: AbstractControl, stepperGroupName: string, formFieldName: string, formFieldGroup: FormControlModel[]) {
    this.addNewFieldGroup(i, formArrayControls, formFieldName, formFieldGroup);
    this.disableAddRow[i + 1] = true;
    if (formFieldGroup && formFieldName === AssetMetadata.General.DOMAINS) {
      formFieldGroup.slice(1).forEach((formField) => {
        formField.options = [];
      });
    }
    if (formFieldGroup && formFieldName === AssetMetadata.Rights.USAGE_RIGHTS) {
      this.adjustBuyoutRightOptions(stepperGroupName, formFieldName, formFieldGroup);
    }
    if (formFieldName === AssetMetadata.Rights.USAGE_RIGHTS || formFieldName === AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY) {
      setTimeout(() => {
        if (!this.stepperModel.editable) this.scrollToBottom();
      }, 100);
    }
  }

  removeRow(
    index: number,
    formArrayControls: AbstractControl,
    step: StepperGroupModel,
    formFieldName: string,
    formFieldGroup?: FormControlModel[],
    parentRowIndex?: number,
  ) {
    const currentFormArray = formArrayControls.get(formFieldName) as UntypedFormArray;
    if (!currentFormArray) return;

    delete this.disableAddRow[index];

    const isSingleRow = currentFormArray.length === 1;

    if (isSingleRow && this.brandingKey === CustomClients.Standard) {
      this.showAssetUploadBuyoutRightsButton = false;
      this.removeSingleStandardRow(index, currentFormArray, step, formFieldName, formFieldGroup);
      return;
    }

    if (currentFormArray.length > 1) {
      currentFormArray.removeAt(index);

      if (formFieldName === AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY)
        this.resetSecondLevelBuyoutRightsTypeIndexes(this.selectedSecondLevelBuyoutRightsType, index);

      if (this.assetUploadBuyoutRights.length > 0 && this.assetUploadBuyoutRightsEditMode.editMode) {
        this.deletedBuyoutRightIndex$.next(index);
      }

      this.emitDeleteEvents(formFieldName, index, parentRowIndex);

      this.cleanupFormFieldVisibility(formFieldGroup, index);

      if (this.brandingKey === CustomClients.Vorwerk) {
        this.handleVorwerkCleanup(index, currentFormArray);
      }

      if (this.brandingKey === CustomClients.SEAT) {
        this.handleSeatBrandingBehavior(step.stepperGroupName, formFieldName, formFieldGroup);
      }
    } else {
      this.matSelectSearchComp?.clearLabel();
    }
  }

  private removeSingleStandardRow(
    index: number,
    formArray: UntypedFormArray,
    step: StepperGroupModel,
    formFieldName: string,
    formFieldGroup?: FormControlModel[],
  ): void {
    formArray.removeAt(index);
    delete this.selectedSecondLevelBuyoutRightsType[index];

    const fieldIndex = step.formFields?.findIndex((f) => f.formFieldName === formFieldName);
    if (fieldIndex !== undefined && fieldIndex !== -1) {
      step.formFields?.splice(fieldIndex, 1);
    }

    this.cleanupFormFieldVisibility(formFieldGroup, index);
  }

  private cleanupFormFieldVisibility(formFieldGroup: FormControlModel[] | undefined, index: number): void {
    formFieldGroup?.forEach((field) => {
      if (field.fieldType !== AssetMetadata.Form.FORM_ARRAY) {
        this.formFieldVisibility[field.formFieldName].splice(index, 1);
      }
    });
  }

  private emitDeleteEvents(formFieldName: string, index: number, parentRowIndex?: number): void {
    if (formFieldName === AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY) {
      this.deletedBuyoutRightIndex.emit(index);
    }

    if (formFieldName === AssetMetadata.Form.TERRITORIES_FORM_ARRAY && parentRowIndex !== undefined) {
      this.deletedBuyoutRightTerritoryIndex.emit({ parentRowIndex, territoryIndex: index });
    }
  }

  private handleVorwerkCleanup(index: number, formArray: UntypedFormArray): void {
    formArray.controls.forEach((control) => {
      const group = control as FormGroup;
      // Loop through each control within the form group and mark dirty
      Object.keys(group.controls).forEach((controlName) => {
        group.get(controlName)?.markAsDirty();
      });
    });

    delete this.brandListMapping[index];
    const newMapping: Record<number, AnyType> = {};
    let counter = 0;
    for (const key in this.brandListMapping) {
      if (+key > index) {
        newMapping[counter] = this.brandListMapping[key];
      }
      counter++;
    }
    this.brandListMapping = newMapping;
  }

  private handleSeatBrandingBehavior(stepperGroupName: string, formFieldName: string, formFieldGroup?: FormControlModel[]): void {
    if (formFieldName === AssetMetadata.Rights.USAGE_RIGHTS) {
      this.adjustBuyoutRightOptions(stepperGroupName, formFieldName, formFieldGroup);
    } else {
      this.fieldChange.emit({
        initialStepper: this.initialStepper,
        formFieldName: AssetMetadata.Product.BRAND_MODEL,
        formGroupField: { stepperGroupName },
      });
    }
  }

  resetSecondLevelBuyoutRightsTypeIndexes(obj: FormArrayMapping, deleteIndex: number): void {
    const totalKeys = Object.keys(obj).length;

    for (let i = deleteIndex; i < totalKeys - 1; i++) {
      obj[i] = obj[i + 1];
    }

    delete obj[totalKeys - 1];
  }

  adjustBuyoutRightOptions(stepperGroupName: string, formFieldName: string, formFieldGroup?: FormControlModel[]) {
    const currentFormArray = this.stepperFormGroups.controls[stepperGroupName].get(formFieldName) as UntypedFormArray;
    const buyoutRightsTitleFieldIndex = formFieldGroup?.findIndex(
      (field: FormControlModel) => field.formFieldName === AssetMetadata.Rights.BUYOUT_RIGHTS_TITLE,
    );
    if (formFieldGroup && buyoutRightsTitleFieldIndex != undefined && buyoutRightsTitleFieldIndex != -1) {
      const buyoutRightsTitleField = formFieldGroup[buyoutRightsTitleFieldIndex];
      const allOptions = buyoutRightsTitleField.options;
      let allAvailableOptions: AnyType[] = [];
      if (allOptions) {
        allAvailableOptions = [
          ...allOptions.map((option) => {
            return { ...option };
          }),
        ];
      }
      const selectedBuyoutRightsTitleValues = currentFormArray.value;
      if (selectedBuyoutRightsTitleValues?.length > 0 && allAvailableOptions.length > 0) {
        selectedBuyoutRightsTitleValues.forEach((buyoutRight: AnyType, index: number) => {
          const newArray = allAvailableOptions.filter((option) => {
            const valueIndex = selectedBuyoutRightsTitleValues.findIndex(
              (buyoutRight: AnyType) => buyoutRight[AssetMetadata.Rights.BUYOUT_RIGHTS_TITLE]._id === option._id,
            );
            if (valueIndex !== index && valueIndex !== -1 && valueIndex !== undefined) {
              return false;
            }
            return true;
          });
          if (!buyoutRightsTitleField.nestedOptions) {
            buyoutRightsTitleField.nestedOptions = [...allAvailableOptions];
          }
          buyoutRightsTitleField.nestedOptions[index] = [...newArray];
        });
      }
    }
  }

  getRequiredErrorMessage(formArrayControls: AbstractControl<AnyType, AnyType>, controlName: string) {
    return formArrayControls.get(controlName)?.hasError('required');
  }

  showSelectedStepper(stepDetails: StepperGroupModel, index: number, silent = false) {
    if (index !== 0) {
      const groupName = this.initialStepper.stepper[index - 1].stepperGroupName;
      const control = this.stepperFormGroups.controls[groupName];
      if (control?.invalid) {
        this.handleInvalidControl(control);
        return;
      }
      this.formControlValidState = false;
      if (control) {
        this.patchBuyoutRightsSecondLevelTypeControl(control);
      }
    }
    this.stepperHeader = !stepDetails.hideHeader ? stepDetails.stepperName : '';
    if (stepDetails.overrideHeader) {
      this.stepperHeader = stepDetails.overrideHeader;
    }
    this.stepperDialogService.resetAllSteps(this.initialStepper, index);

    if (!silent) {
      this.stepChange.emit(stepDetails);
    }
  }

  private patchBuyoutRightsSecondLevelTypeControl(control: AbstractControl, callFrom?: string) {
    const buyoutRightsSecondLevelTypeControl = control.get(AssetMetadata.Rights.BUYOUT_RIGHTS_SECOND_LEVEL_TYPE);
    if (!buyoutRightsSecondLevelTypeControl) return;
    if (callFrom === 'assetUpload') {
      const secondLevelTypeControlValue = buyoutRightsSecondLevelTypeControl.value;
      const mergedValues = mergeSecondLevelBuyoutRightsType(secondLevelTypeControlValue, this.selectedSecondLevelBuyoutRightsType);
      buyoutRightsSecondLevelTypeControl.patchValue(mergedValues);
    } else {
      buyoutRightsSecondLevelTypeControl.patchValue(this.selectedSecondLevelBuyoutRightsType);
    }
  }

  private formInvalidState(control: AbstractControl<AnyType, AnyType>) {
    control.markAllAsTouched();
    control.markAsDirty();
    this.fileUploadRequired = true;
    this.isMatTreeSelected = true;
  }

  private scrollIntoView(control: AbstractControl<AnyType, AnyType>) {
    const firstRequiredControl = this.commonUtilityLib.getRequiredControls(control as UntypedFormGroup);
    if (firstRequiredControl) {
      const invalidFormField = this.el.nativeElement.querySelector('[id="stepper-dialog_' + firstRequiredControl + '"]');
      if (invalidFormField) invalidFormField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  private handleInvalidControl(control: AbstractControl, context: 'saveToTemp' | 'default' = 'default'): void {
    this.scrollIntoView(control);
    if (context === 'saveToTemp') {
      this.formControlSaveToTempValidState = true;
    } else {
      this.formControlValidState = true;
    }
    this.formInvalidState(control);
  }

  /**
   * Move to next step
   * @param action
   * @param control
   * @returns
   */
  async nextGoToStep(action: ActionsModel, control?: AbstractControl<AnyType, AnyType>, noValidationFlag = false) {
    this.beforeStepChange.emit();

    if (control?.invalid) {
      this.handleInvalidControl(control);
      return;
    }
    this.formControlValidState = false;
    this.imgData = {};
    switch (action && action?.actionType) {
      case 'uploadAsset': {
        if (control) control.patchValue({ disabled: true });
        const formValueObject = this.stepperDialogService.getFormDirtyValues(this.stepperFormGroups);
        this.formSubmit.emit(formValueObject);
        break;
      }
      case 'saveBuyoutRights': {
        if (control) control.patchValue({ disabled: true });
        const formValueObject = this.stepperDialogService.getFormDirtyValues(this.stepperFormGroups);
        addSubLevelTypeToBuyoutRight(formValueObject);
        this.formSubmit.emit(formValueObject);
        break;
      }
      case 'selectAssets': {
        const assetGalleryControl = control?.get('assetGallery');
        let goTo = (action?.goToStep || 0) - 1;
        // if no asset selected, skip the step
        if (assetGalleryControl && assetGalleryControl.value?.length <= 0) {
          goTo = goTo + 1;
        }
        const stepDetails = this.initialStepper.stepper[goTo];
        this.showSelectedStepper(stepDetails, goTo);
        this.stepChange.emit(stepDetails);
        break;
      }
      case AssetMetadata.Rights.BUYOUT_RIGHTS: {
        const goTo = (action?.goToStep || 0) - 1;
        const stepDetails = this.initialStepper.stepper[goTo];
        this.showSelectedStepper(stepDetails, goTo);
        const buyoutRightsFormArray = control?.get(AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY) as UntypedFormArray;
        if (this.isCallFromAssetUpload(buyoutRightsFormArray)) {
          const stepperIndex = this.initialStepper.stepper.findIndex((step) => step.stepperGroupName === AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION);
          if (stepperIndex !== -1) this.saveToTempForm(this.initialStepper.stepper[stepperIndex]);
        }
        this.stepChange.emit(stepDetails);
        break;
      }
      case AssetMetadata.Collections.COLLECTION: {
        const collection = control?.get(AssetMetadata.Others.SELECT_TYPE);
        if (collection?.value !== '') {
          const isCreateNewCollection = collection?.value?.id === 'createNewCollection';
          await this._addCollectionMetadataFormGroup(isCreateNewCollection);
          const stepDetails = this.initialStepper.stepper[this.collectionNextStepIdx];
          this.showSelectedStepper(stepDetails, this.collectionNextStepIdx);
          this.stepChange.emit(stepDetails);
        } else {
          // if no collection selected, skip the step and submit the form
          if (control) control.patchValue({ disabled: true });
          const formValueObject = this.stepperDialogService.getFormDirtyValues(this.stepperFormGroups);
          this.formSubmit.emit(formValueObject);
        }
        break;
      }
      default: {
        const stepDetails = this.initialStepper.stepper[(action?.goToStep || 0) - 1];
        this.showSelectedStepper(stepDetails, (action?.goToStep || 0) - 1);
        this.stepChange.emit(stepDetails);
        break;
      }
    }
  }

  isCallFromAssetUpload(buyoutRightsFormArray: UntypedFormArray): boolean {
    return this.stepperModel.callFrom === 'assetUpload' && this.brandingKey === CustomClients.Standard && !!buyoutRightsFormArray?.length;
  }

  /**
   * Generate collection steps and form group
   * @param collectionStep
   * @param stepperLength
   * @param removeControl
   * @returns
   */
  collectionStep(collectionStep: StepperGroupModel, stepperLength: number, removeControl: string) {
    let formGroup: FormGroup = {} as FormGroup;
    collectionStep.stepperId = (stepperLength + 1).toString();
    // update back button gotoStep value
    if (collectionStep.actions) collectionStep.actions[1].goToStep = +this.initialStepper.stepper[stepperLength - 1].stepperId;
    formGroup = this.formService.getFormGroupFor(collectionStep)[0];
    this.stepperFormGroups.removeControl(removeControl);
    return { collectionStep, formGroup };
  }

  /**
   * Add collection stepper (create new and add to existing) config to parent stepper config;
   * Add the collection stepper control to stepperFormGroups
   * @param isCreateNewCollection
   */
  private async _addCollectionMetadataFormGroup(isCreateNewCollection: boolean) {
    const stepperLength = this.initialStepper.stepper.length;
    let step: {
      collectionStep: StepperGroupModel;
      formGroup: FormGroup;
    };
    if (isCreateNewCollection) {
      const assetListData: AnyType = this.assetListFilesDraft?.length > 0 ? this.assetListFilesDraft : this.assetListFiles;
      const createCollectionStep = await this.assetSettingsStepperConfigService.getCreateNewCollectionStepperConfig(assetListData, this.brandingKey);
      if (this.brandingKey === this.CustomClients.LufthansaMediacenter || this.brandingKey === this.CustomClients.LufthansaMedialounge) {
        const formFieldIndex =
          createCollectionStep &&
          createCollectionStep.formFields?.findIndex((formField: FormControlModel) => formField.formFieldName === AssetMetadata.Others.KEYWORDS);
        if (createCollectionStep.formFields && formFieldIndex != undefined && formFieldIndex != -1) {
          createCollectionStep.formFields[formFieldIndex].placeholder = $localize`:placeholder:Enter tags`;
          createCollectionStep.formFields[formFieldIndex].fieldLabel = $localize`Tags`;
        }
        if (this.brandingKey === this.CustomClients.LufthansaMedialounge) {
          const descriptionIndex =
            createCollectionStep && createCollectionStep.formFields?.findIndex((formField: FormControlModel) => formField.formFieldName === 'description');
          if (descriptionIndex != -1 && descriptionIndex != undefined && createCollectionStep && createCollectionStep.formFields) {
            delete createCollectionStep.formFields[descriptionIndex].validators;
          }
        }
      }
      step = this.collectionStep(createCollectionStep, stepperLength, AssetMetadata.Collections.ADD_TO_EXISTING);
    } else {
      const addToExistingCollectionStep = getAddToExistingCollectionStepperConfig();
      step = this.collectionStep(addToExistingCollectionStep, stepperLength, AssetMetadata.Collections.CREATE_NEW);
    }
    this.initialStepper.stepper.splice(this.collectionNextStepIdx, 1, step.collectionStep);
    this.stepperFormGroups.addControl(step.collectionStep.stepperGroupName, step.formGroup);

    if (this.stepperFormGroups.controls[AssetMetadata.Collections.CREATE_NEW]) {
      this.keywordSub = this.assetTagsService
        .fetchTagsForAutoComp(this.stepperFormGroups.controls[AssetMetadata.Collections.CREATE_NEW].get(AssetMetadata.Others.KEYWORDS))
        ?.pipe(tap((data) => (this.autoCompTags = data)))
        .subscribe();
    }

    if (!isCreateNewCollection) {
      // set option on add to existing stepper
      this.setOption(this.initialStepper, this.collectionsList);
    }
  }

  /**
   * Populate add to existing stepper option
   * @param initialStepper
   * @param collectionList
   */
  setOption(initialStepper: StepperModel, collectionList: Collection[]) {
    const collectionListOptions = initialStepper.stepper[this.collectionNextStepIdx].formFields?.[0];
    if (collectionListOptions) {
      collectionListOptions.options = collectionList.map((collection: Collection) => {
        return {
          id: collection._id,
          name: collection.name,
          imageUrl: collection.assetData ? collection.assetData.previewUrl : './assets/placeholder-collection-small.png',
        };
      });
    }
  }

  /**
   * check for field validation
   * @param formField
   * @returns
   */
  getFieldValidation(formField: AbstractControl<AnyType, AnyType> | null): boolean {
    return formField?.errors?.['required'] && formField?.touched;
  }

  /**
   * Add tags
   * @param event
   * @param stepperGroupName
   * @param fieldName
   * @returns
   */
  async add(event: AnyType, stepperGroupName: string, fieldName: string) {
    if (!event || this.tags.includes(event)) return;

    if (this.environment.feature?.tagGroups === true && event._id) {
      await this.coreAssetsTagsService.handleTagAddition(event, this.tags);
    } else {
      addTagToArray(event, this.tags);
    }

    this.updateTagsArray(this.tags, stepperGroupName, fieldName);
  }

  /**
   * Add collection tags
   * @param event
   * @param stepperGroupName
   * @param fieldName
   * @returns
   */
  async addKeyword(event: AnyType, stepperGroupName: string, fieldName: string) {
    if (!event || this.collectionTags.includes(event)) return;

    if (this.environment.feature?.tagGroups === true && event._id) {
      await this.coreAssetsTagsService.handleTagAddition(event, this.collectionTags);
    } else {
      addTagToArray(event, this.collectionTags);
    }

    this.updateTagsArray(this.collectionTags, stepperGroupName, fieldName, { refreshUI: false });
  }

  /**
   * Update tags array in the form group and service
   * @param tagsArray
   * @param stepperGroupName
   * @param fieldName
   */
  private updateTagsArray(tagsArray: string[], stepperGroupName: string, fieldName: string, options = { refreshUI: true }) {
    const formGroup = this.stepperFormGroups.controls[stepperGroupName];

    this.coreAssetsTagsService.updateTagsArray(tagsArray, formGroup, fieldName);

    if (options.refreshUI) this.refreshTagList();
  }

  private refreshTagList() {
    if (this.tagsChipComponent) this.tagsChipComponent.fetchTagsWithAssetCount();
    if (this.tagsChipsGridRow) this.tagsChipsGridRow.chipInput.nativeElement.value = '';
  }

  tagOptionChanged(evt: AnyType, stepperGroupName: string, fieldName: string) {
    if (evt.fieldSelected) {
      if (stepperGroupName === AssetMetadata.Collections.CREATE_NEW) {
        this.addKeyword(evt.name, stepperGroupName, fieldName);
      } else {
        this.add(evt.name, stepperGroupName, fieldName);
      }
    } else {
      if (evt.createdAt) {
        this.removeTag(evt.name, stepperGroupName, fieldName);
      } else {
        if (stepperGroupName === AssetMetadata.Collections.CREATE_NEW) {
          this.addKeyword(evt.name, stepperGroupName, fieldName);
        } else {
          this.add(evt.name, stepperGroupName, fieldName);
        }
      }
    }
  }

  /**
   * Pagination change event
   * @param event
   */
  onPaginateChange(event: PageEvent) {
    this.paginatorObj = event;
  }

  removeTagOnChange(evt: AnyType, stepperGroupName: string, fieldName: string) {
    this.removeTag(evt.list.name || evt.list, stepperGroupName, fieldName);
  }

  /**
   * Remove tags / keywords
   * @param key
   * @param stepperGroupName
   * @param fieldName
   */
  removeTag(key: string, stepperGroupName: string, fieldName: string): void {
    const tagsRef = stepperGroupName === AssetMetadata.Collections.CREATE_NEW ? this.collectionTags : this.tags;
    const index = tagsRef.indexOf(key);
    if (index >= 0) {
      // update tags list in TagsChipComponent on tag removal from ChipsGridRowComponent
      if (this.tagsChipComponent) this.tagsChipComponent.removeSelectedTags(key);
      tagsRef.splice(index, 1);
      tagsRef.sort((a, b) => a.localeCompare(b));
      this.stepperFormGroups.controls[stepperGroupName].patchValue({
        [fieldName]: tagsRef,
      });
      this.assetTagsService.updateTagsArray(tagsRef);
    }
  }

  /**
   * Remove chip from multi select
   * @param value
   */
  removeMultiSelectChip(value: AnyType, stepperGroupName: string, fieldName: string, i?: number, formArrayFieldName?: string) {
    if (i != undefined && formArrayFieldName) {
      const currentFormArray = this.stepperFormGroups.controls[stepperGroupName].get(fieldName) as UntypedFormArray;
      const chip = currentFormArray.at(i).get(formArrayFieldName)?.value;
      const modifiedChip = this.commonUtilityLib.removeObjectFromArray(chip, value.list.id || value.list._id || value.list);
      currentFormArray.at(i).get(formArrayFieldName)?.setValue(modifiedChip);
    } else {
      const chip = this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.value;
      const modifiedChip = this.commonUtilityLib.removeObjectFromArray(chip, value.list.id || value.list._id || value.list);
      this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.setValue(modifiedChip);
    }
  }

  removeMultiSelectChipAtSecondLevel(value: AnyType, FormArrayControls: AbstractControl) {
    const formControlName = value.formControlName;
    const formField = FormArrayControls.get(formControlName);
    if (formField) {
      const chip = formField?.value;
      const modifiedChip = this.commonUtilityLib.removeObjectFromArray(chip, value.list.id || value.list._id || value.list);
      formField.setValue(modifiedChip);
    }
  }

  /**
   * mat-checkbox capture change event
   * @param event
   * @param stepperGroup
   * @param formFieldName
   */
  checkboxChanged(event: MatCheckboxChange, stepperGroup: string, formFieldName: string) {
    this.stepperFormGroups.controls[stepperGroup].patchValue({
      [formFieldName]: event.checked,
    });

    const formGroupField = this.stepperFormGroups.controls[stepperGroup] as UntypedFormGroup;
    const formField = formGroupField?.controls[formFieldName];

    this.fieldChange.emit({ event, formFieldName, formField, formGroupField });
  }

  /**
   * mat-checkbox capture change event for formArray
   * @param event
   * @param stepperGroup
   * @param formFieldName
   */
  formArrayCheckboxChanged(
    event: MatCheckboxChange,
    i: number,
    formArrayControls: AbstractControl<AnyType, AnyType>,
    formField: FormControlModel,
    stepperGroupName?: string,
  ) {
    if (event.checked) {
      formArrayControls.get(AssetMetadata.Rights.EXPIRY_DATE)?.removeValidators([Validators.required]);
      formArrayControls.get(AssetMetadata.Rights.EXPIRY_DATE)?.patchValue('');
      formArrayControls.get(AssetMetadata.Rights.EXPIRY_DATE)?.markAsDirty();
      formArrayControls.get(AssetMetadata.Rights.EXPIRY_DATE)?.disable();
      formArrayControls.get(AssetMetadata.Rights.EXPIRY_DATE)?.updateValueAndValidity();
      this.disableAddRow[i] = false;
    } else {
      formArrayControls.get(AssetMetadata.Rights.EXPIRY_DATE)?.addValidators([Validators.required]);
      formArrayControls.get(AssetMetadata.Rights.EXPIRY_DATE)?.enable();
      formArrayControls.get(AssetMetadata.Rights.EXPIRY_DATE)?.updateValueAndValidity();
      this.changeDisableAddRowButton(i, formField.formFieldName, this.stepperFormGroups.controls[stepperGroupName || '']);
    }
  }

  copyFormArrayFromAbove(
    firstLevelRowIndex: number,
    event: MatCheckboxChange,
    formArrayControls: AbstractControl,
    fieldArrayName: string,
    formArrayField: string,
    formFieldGroup: FormControlModel[],
  ) {
    if (event.checked) {
      this.isCopiedFormArrayChecked[firstLevelRowIndex] = event.checked;
      this.aTopTerritories = (formArrayControls.get(fieldArrayName) as UntypedFormArray)
        .at(firstLevelRowIndex - 1)
        .get(formArrayField)
        ?.getRawValue();
      const currentFormArray = (formArrayControls.get(fieldArrayName) as UntypedFormArray).at(firstLevelRowIndex).get(formArrayField) as UntypedFormArray;
      if (this.aTopTerritories) {
        this.aTopTerritories.forEach((formArray: AnyType, i: number) => {
          let formGroup = currentFormArray?.at(i) as FormGroup;
          if (!formGroup) {
            this.addNewFieldGroup(i, (formArrayControls.get(fieldArrayName) as UntypedFormArray).at(firstLevelRowIndex), formArrayField, formFieldGroup);
            formGroup = currentFormArray?.at(i) as FormGroup;
          }

          formGroup?.patchValue(formArray);
          Object.keys(formGroup.controls).forEach((controlName) => {
            // If the current property is EXPIRY_DATE and NO_EXPIRY is truthy,
            // disable and update the value and validity of the EXPIRY_DATE property.
            if (controlName === AssetMetadata.Rights.EXPIRY_DATE && formGroup.get(AssetMetadata.Rights.NO_EXPIRY)?.value) {
              this.updateExpiryDateControlOnNoExpiry(formGroup);
            }
            formGroup.get(controlName)?.markAsDirty();
            formGroup.get(controlName)?.markAsTouched();
            formGroup.get(controlName)?.updateValueAndValidity();
          });
        });
      }
    } else {
      this.aTopTerritories = null;
    }
  }

  /**
   * If mat tree status changes (checked or unchecked)
   * @param event
   * @param stepperGroupName
   * @param fieldName
   */
  changeStatus(event: boolean, stepperGroupName: string, fieldName: string) {
    this.disableButtons = !event;
    if (!event) {
      this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.setErrors({ required: true });
    } else {
      this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.setErrors(null);
    }
  }

  /**
   * MaT tree returns checked nodes
   * @param event
   */
  selectedFormatList(event: AspectRationFormatTree[]) {
    this.matTreeRawData = event;
    if (this.matTreeRawData.length) this.isMatTreeSelected = false;
  }

  /**
   * Set values in listUsers from sp-user-email-input-autocomplete component
   * @param user
   */
  userSelected(user: string[]) {
    this.listUsers = user;
  }

  /**
   * store tagged user list when user tagged get at sp-editor
   * @param userList
   */
  taggedUserList(userList: UserInfo[]) {
    this.taggedUser.emit(userList);
  }

  /**
   * store description text from sp-editor change
   * @param descriptionText
   */
  editorDescriptionText(descriptionText: string) {
    this.descriptionText.emit(descriptionText);
  }

  /*-------------------- File upload and assets creation funcs ---------------------*/

  updatedFilesList(event: { files: FileObject[] | AnyType[]; removedAsset: AnyType }, formField: FormControlModel) {
    this.files = event.files;

    this.fieldChange.emit({ event: event.files, formFieldName: formField.formFieldName, formField });
  }

  selectedFiles(event: FileObject[], formField: FormControlModel) {
    this.files = event;
    this.disableButtons = false;
    this.fileUploadRequired = false;
    this.fieldChange.emit({ event, formFieldName: formField.formFieldName, formField });
  }

  /**
   * Fetch all the collections
   */
  fetchAllCollections() {
    const filterObject = this.stepperDialogService.getFilter(this.currentUserID);

    // call the api
    this.collectionAPIService
      .collectionsControllerFindAll(JSON.stringify(filterObject))
      .pipe(
        map((collectionsResult: CollectionResultResponse) => collectionsResult.result),
        filter((collections: Collection[]) => collections && !!collections.length),
      )
      .subscribe(async (collectionList: Collection[]) => {
        this.collectionsList = collectionList;
        this._getAssets();
      });
  }

  editorInput(event?: AnyType) {
    this.isEditorError = (event ? event.srcElement : document.getElementById('textareaEditor'))?.innerText.toString().trim().length;
  }

  /**
   * Get all assets
   */
  private _getAssets() {
    // get the asset ids
    const assetsIds = this.stepperDialogService.getAssetIDsFromCollections(this.collectionsList);

    // call the api
    this.assetApiService
      .assetsControllerFindMany({ ids: assetsIds })
      .pipe(map((searchResult: AssetSearchResult) => searchResult.result || []))
      .subscribe(async (assets: AssetUIModel[]) => {
        if (this.assetUrlService) {
          // generate preview urls
          assets = await this.assetUrlService.generatePreview(assets);

          // update the collections
          this.stepperDialogService.updateCollectionsAssets(assets, this.collectionsList);
        }
      });
  }

  /**
   * Assign paginator to MatTableDataSource paginator
   */
  paginatorAssignment(event: { selectedTagsList: TagsWithAssetCount[]; disableApply?: boolean }) {
    setTimeout(() => {
      this.dataSource = new MatTableDataSource<TagsWithAssetCount>(event.selectedTagsList);
      this.dataSource.paginator = this.paginator;
      this.tagsObs = this.dataSource.connect();
    });
  }

  searchedTags(event: { searchTagsList: TagsWithAssetCount[]; selectedTagName?: string }) {
    this.searchTagsList = event.searchTagsList;
  }

  inputBlurOut() {
    this.tagsChipComponent.fetchTagsWithAssetCount();
  }

  trimFormControlValue(controlName: string, groupName: string) {
    const control = this.stepperFormGroups.controls[groupName].get(controlName);
    this.formControlValidState = control?.status === 'INVALID';
    if (control && typeof control.value === 'string') {
      control.setValue(control.value.trim());
    }
  }

  inputValueChanged(event: AnyType, inputField: string, stepperGroupName: string) {
    if (stepperGroupName === 'generalInformation' && inputField === 'name') {
      this.nameFieldChange.emit(event.target.value);
    }
  }

  isLastIndex(index: number, formArrayControls: AbstractControl, formFieldName: string): boolean {
    const currentFormArray = formArrayControls.get(formFieldName) as UntypedFormArray;
    return index === currentFormArray?.value?.length - 1;
  }

  restrictInput(event: KeyboardEvent): void {
    const allowedPattern = /^[0-9-]$/; // Allow only numbers and hyphen
    if (!allowedPattern.test(event.key)) {
      event.preventDefault();
    }
  }

  scrollToBottom(): void {
    if (this.sectionContainers.length > 0) {
      // Scroll to the last added section
      const lastSection = this.sectionContainers.last.nativeElement;
      lastSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  isEmptySelectedSecondLevelBuyoutRightsType() {
    if (Object.keys(this.selectedSecondLevelBuyoutRightsType).length === 0) {
      return false;
    }
    return true;
  }

  shouldDisplayFormArrayField(
    formFieldName: string,
    formArrayFieldName: string,
    firstLevelRowIndex: number,
    stepperGroupName: string,
    formField: FormControlModel,
  ): boolean {
    const buyoutRightSingleStepper = this.environment.uploadStepperConf?.buyoutRightSingleStepper;

    return (
      !buyoutRightSingleStepper ||
      formFieldName === 'domains' ||
      (buyoutRightSingleStepper && formArrayFieldName === 'buyoutRightsTitle') ||
      (this.brandingKey !== CustomClients.SEAT &&
        (!this.formFieldVisibility[formArrayFieldName] || this.formFieldVisibility[formArrayFieldName][firstLevelRowIndex])) ||
      (this.brandingKey === CustomClients.SEAT &&
        this.checkBuyoutRightsTitleRequiredValue(firstLevelRowIndex, formField, stepperGroupName, formFieldName, 'buyoutRightsTitle', formArrayFieldName, ''))
    );
  }

  saveToTempForm(step: StepperGroupModel) {
    const generalInfoCtrl = this.stepperFormGroups.controls[AssetMetadata.Rights.USAGE_RIGHTS_GENERAL_INFORMATION];
    const buyoutRightsTitleId: string = generalInfoCtrl.getRawValue()?.buyoutRightsTitle?._id;
    if (generalInfoCtrl?.invalid) {
      this.handleInvalidControl(generalInfoCtrl, 'saveToTemp');
      return;
    }

    this.formControlSaveToTempValidState = false;
    this.showAssetUploadBuyoutRightsButton = false;
    this.disableBuyoutRightsTitle = false;

    if (this.assetUploadBuyoutRightsEditMode.editMode) {
      this.updateExistingBuyoutRights(generalInfoCtrl);
    } else {
      this.addOrMergeNewBuyoutRights(generalInfoCtrl, buyoutRightsTitleId);
    }

    this.cleanupGeneralInfoForm(generalInfoCtrl);

    updateFormFieldConfig(step);
  }

  private updateExistingBuyoutRights(generalInfoCtrl: AbstractControl): void {
    const editIndex = this.assetUploadBuyoutRightsEditMode.editIndex;
    const existingFormArray = this.assetUploadBuyoutRights.at(editIndex).get(AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY) as UntypedFormArray;

    const deletedIndex = this.deletedBuyoutRightIndex$.getValue() || -1;
    if (deletedIndex !== -1) {
      existingFormArray.removeAt(deletedIndex);
      this.patchBuyoutRightsSecondLevelTypeControl(this.assetUploadBuyoutRights.at(editIndex));
    }

    const newFormArray = generalInfoCtrl.get(AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY) as UntypedFormArray;
    newFormArray.controls.forEach((ctrl, i) => {
      if (existingFormArray.at(i)) {
        existingFormArray.at(i).patchValue(ctrl.getRawValue());
      } else {
        existingFormArray.push(ctrl);
      }
    });

    this.assetUploadBuyoutRightsEditMode = { editMode: false, editIndex: -1 };
  }

  private addOrMergeNewBuyoutRights(generalInfoCtrl: AbstractControl, buyoutRightsTitleId: string): void {
    const clonedControl = deepCloneControl(generalInfoCtrl);
    const existingIndex = this.assetUploadBuyoutRights.getRawValue()?.findIndex((right) => right.buyoutRightsTitle._id === buyoutRightsTitleId);

    if (existingIndex !== -1) {
      const sourceArray = clonedControl.get(AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY) as UntypedFormArray;
      const targetArray = this.assetUploadBuyoutRights.at(existingIndex).get(AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY) as UntypedFormArray;
      sourceArray.controls.forEach((ctrl) => targetArray.push(ctrl));
      this.patchBuyoutRightsSecondLevelTypeControl(this.assetUploadBuyoutRights.at(existingIndex), 'assetUpload');
    } else {
      this.patchBuyoutRightsSecondLevelTypeControl(clonedControl);
      this.assetUploadBuyoutRights.push(clonedControl);
    }
  }

  private cleanupGeneralInfoForm(control: AbstractControl): void {
    const formGroup = control as UntypedFormGroup;
    formGroup.removeControl(AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY);
    formGroup.removeControl(AssetMetadata.Rights.BUYOUT_RIGHTS_SECOND_LEVEL_TYPE);

    const titleControl = formGroup.get(AssetMetadata.Rights.BUYOUT_RIGHTS_TITLE);
    titleControl?.reset();
    titleControl?.enable();

    if (this.assetUploadBuyoutRights.length > 0) {
      titleControl?.removeValidators([Validators.required]);
      titleControl?.updateValueAndValidity();
    }
  }

  returnBuyoutRightsTypeIndex(firstLevelRowIndex: number, selectedBuyoutRightsType: string) {
    const nextRowIndex = firstLevelRowIndex + 1;

    if (this.assetUploadBuyoutRightsEditMode.editMode) {
      return nextRowIndex;
    }

    const existingIndex = this.assetUploadBuyoutRights.getRawValue()?.findIndex((right) => right.buyoutRightsTitle.name === selectedBuyoutRightsType);

    if (existingIndex !== -1) {
      const formArray = this.assetUploadBuyoutRights.at(existingIndex).get(AssetMetadata.Rights.BUYOUT_RIGHTS_FORM_ARRAY) as UntypedFormArray;
      return formArray.length + nextRowIndex;
    }

    return nextRowIndex;
  }

  onDeleteBuyoutRight(index: number, buyoutRightName: string): void {
    this.confirmDialog
      .open({
        title: $localize`Delete <b>Buyout rights: ${buyoutRightName}</b>`,
        message: $localize`It will NOT be possible to recover the information after deleting.`,
        confirmCaption: $localize`:dialog button:Delete`,
        confirmCaptionIcon: 'delete',
        confirmCaptionCssStyle: '!justify-start',
      })
      .subscribe(async (data) => {
        if (data) this.assetUploadBuyoutRights.removeAt(index);
      });
  }

  onEditBuyoutRights(index: number): void {
    const assetUploadBuyoutRightsValue = this.assetUploadBuyoutRights.at(index).getRawValue();
    addSubLevelTypeToBuyoutRight({ usageRightsGeneralInformation: assetUploadBuyoutRightsValue });
    const formattedValues = this.assetCommonService.constructMakeOSUsageRights({ usageRightsGeneralInformation: assetUploadBuyoutRightsValue });
    this.patchMakeOsBuyoutRights(formattedValues?.makeBuyoutRights?.[0] as MakeBuyoutRights);

    this.assetUploadBuyoutRightsEditMode = { editMode: true, editIndex: index };
    this.showAssetUploadBuyoutRightsButton = true;
  }

  openTaggingGuidelines() {
    const pdfUrl = 'assets/tagging_guidelines/Verschlagwortungskonzept.pdf';
    window.open(pdfUrl, '_blank');
  }

  openNamingGuidelines() {
    const pdfUrl = 'assets/naming_guidelines/Benamungskonzept.pdf';
    window.open(pdfUrl, '_blank');
  }

  ngOnDestroy(): void {
    this.formControlSub?.unsubscribe();
    this.tagSub?.unsubscribe();
    this.keywordSub?.unsubscribe();

    this._destroyed$.next();
    this._destroyed$.complete();
  }
}
